import React, { useState, useEffect } from 'react';
import { Trophy, Target, TrendingUp, Users, BarChart3, Shield, Flame, Activity, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useTheme } from '../../../../contexts/ThemeContext';
import StatCard from '../../../pingpong/stats/components/StatCard';
import Tabs from '../../../../components/common/Tabs';
import { pickleballTournamentStatsAPI } from '../services/pickleballTournamentStatsApi';
import { formatPercentage, formatNumber, formatRecord } from '../../../pingpong/stats/utils/statsFormatters';

const SHOT_COLS = [
  { key: 'smash',         label: 'Smash',    color: 'text-red-500' },
  { key: 'ace',           label: 'Ace',       color: 'text-blue-500' },
  { key: 'lob',           label: 'Lob',       color: 'text-indigo-500' },
  { key: 'drop_shot',     label: 'Drop',      color: 'text-teal-500' },
  { key: 'volley_winner', label: 'Volley',    color: 'text-green-500' },
  { key: 'dink_winner',   label: 'Dink',      color: 'text-purple-500' },
];

const ERROR_COLS = [
  { key: 'service_fault',  label: 'Svc Fault', color: 'text-red-600' },
  { key: 'forced_error',   label: 'Forced',    color: 'text-orange-500' },
  { key: 'unforced_error', label: 'Unforced',  color: 'text-yellow-600' },
  { key: 'other_error',    label: 'Other',     color: 'text-gray-400' },
];

const RankBadge = ({ rank, theme }) => (
  <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
    rank === 1 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
    rank === 2 ? 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200' :
    rank === 3 ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
    theme.backgroundSecondary
  }`}>{rank}</span>
);

const FormDots = ({ form }) => (
  <div className="flex gap-1 justify-center">
    {form.map((r, i) => (
      <div key={i} className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        r === 'W' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      }`}>{r}</div>
    ))}
  </div>
);

// Clickable column header — click to sort by this key, click again to flip
// direction. Shows a neutral icon until this column is the active sort key.
const SortableHeader = ({ label, sortKey, sortConfig, onSort, align = 'center', className = '' }) => {
  const isActive = sortConfig.key === sortKey;
  const Icon = isActive ? (sortConfig.direction === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th className={`px-4 py-3 ${align === 'left' ? 'text-left' : 'text-center'} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 select-none hover:opacity-80 ${align === 'left' ? '' : 'justify-center w-full'} ${isActive ? 'text-current' : 'opacity-70'}`}
      >
        {label}
        <Icon className="w-3.5 h-3.5 shrink-0" />
      </button>
    </th>
  );
};

const TournamentPickleballStatsView = ({ tournamentId, tournament }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [sortConfig, setSortConfig] = useState({ key: 'rank', direction: 'asc' });
  const [shotSort, setShotSort] = useState('pointsWon');

  // Numeric/rank-ish columns read better sorted "best first" (descending) on
  // first click; rank and name read better ascending (lowest rank / A-first).
  const ASCENDING_BY_DEFAULT = new Set(['rank', 'name']);
  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: ASCENDING_BY_DEFAULT.has(key) ? 'asc' : 'desc' };
    });
  };

  useEffect(() => {
    if (!tournamentId) return;
    setLoading(true);
    pickleballTournamentStatsAPI.getTournamentStats(tournamentId)
      .then(data => setStats(data))
      .catch(err => console.error('Failed to fetch pickleball stats:', err))
      .finally(() => setLoading(false));
  }, [tournamentId, tournament?.name]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className={`${theme.card} rounded-lg border ${theme.border} p-8`}>
        <p className={`text-center ${theme.textMuted}`}>
          No statistics available yet. Complete some matches to see tournament stats!
        </p>
      </div>
    );
  }

  // Signed so a 3-game win streak sorts above a 1-game win streak, which
  // sorts above a 1-game loss streak, which sorts above a 3-game loss streak.
  const streakValue = (p) => p.currentStreak.type === 'win' ? p.currentStreak.count : -p.currentStreak.count;

  const SORT_VALUE_GETTERS = {
    rank: (p) => p.rank,
    name: (p) => p.name?.toLowerCase() ?? '',
    played: (p) => p.played,
    wins: (p) => p.wins,
    winRate: (p) => p.winRate,
    gamesWon: (p) => p.gamesWon,
    pointsFor: (p) => p.pointsFor,
    differential: (p) => p.differential,
    streak: streakValue,
  };

  const allParticipants = [...(stats.allPlayerStats || [])].sort((a, b) => {
    const getValue = SORT_VALUE_GETTERS[sortConfig.key] || SORT_VALUE_GETTERS.rank;
    const va = getValue(a);
    const vb = getValue(b);
    const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
    return sortConfig.direction === 'asc' ? cmp : -cmp;
  });

  const shotStats = [...(stats.playerShotStats || [])].sort((a, b) => {
    if (shotSort === 'servingEfficiency') return b.servingEfficiency - a.servingEfficiency;
    if (shotSort === 'totalErrors') return (b.service_fault + b.forced_error + b.unforced_error + b.other_error) - (a.service_fault + a.forced_error + a.unforced_error + a.other_error);
    return (b[shotSort] ?? 0) - (a[shotSort] ?? 0);
  });

  const TAB_DEFS = [
    { id: 'overview', label: 'Overview' },
    { id: 'players',  label: 'All Players' },
    { id: 'shots',    label: 'Shot Stats' },
  ];

  return (
    <div className="space-y-6">
      {tournament && (
        <div>
          <h2 className={`text-2xl font-bold ${theme.text} mb-1`}>Tournament Statistics</h2>
          <p className={theme.textMuted}>{tournament.name || 'Tournament'} · Pickleball</p>
        </div>
      )}

      <Tabs
        tabs={TAB_DEFS}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        showMobileMenu={false}
      />

      {/* ── Overview ── */}
      {activeTab === 'overview' && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Tournament Progress" value={`${stats.completionPercentage}%`} icon={Activity} subtitle={`${stats.completedMatches}/${stats.totalMatches} matches`} color="primary" />
            <StatCard label="Participants" value={stats.totalParticipants} icon={Users} subtitle={`${stats.activeParticipants} active`} color="success" />
            <StatCard label="Games Played" value={stats.totalGamesPlayed} icon={Target} color="warning" />
            <StatCard label="Avg Score Margin" value={stats.averagePointDifferential} icon={BarChart3} subtitle="points per game" color="primary" />
          </div>

          {/* Top Performers */}
          <div>
            <h3 className={`text-lg font-semibold ${theme.text} mb-4`}>Top Performers</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.mostWins && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2"><Trophy className="w-5 h-5 text-yellow-500" /><span className={`text-sm font-medium ${theme.textMuted}`}>Most Wins</span></div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.mostWins.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>{formatRecord(stats.mostWins.wins, stats.mostWins.losses)}</p>
                </div>
              )}
              {stats.topScorer && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2"><Target className="w-5 h-5 text-blue-500" /><span className={`text-sm font-medium ${theme.textMuted}`}>Top Scorer</span></div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.topScorer.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>{formatNumber(stats.topScorer.points)} points</p>
                </div>
              )}
              {stats.highestWinRate && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-5 h-5 text-green-500" /><span className={`text-sm font-medium ${theme.textMuted}`}>Best Win Rate</span></div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.highestWinRate.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>{formatPercentage(stats.highestWinRate.winRate)} ({stats.highestWinRate.record})</p>
                </div>
              )}
              {stats.longestStreak && (
                <div className={`${theme.card} rounded-lg border ${theme.border} p-4`}>
                  <div className="flex items-center gap-2 mb-2"><Flame className="w-5 h-5 text-orange-500" /><span className={`text-sm font-medium ${theme.textMuted}`}>Hottest Streak</span></div>
                  <p className={`text-xl font-bold ${theme.text}`}>{stats.longestStreak.name}</p>
                  <p className={`text-sm ${theme.textMuted}`}>{stats.longestStreak.streak} consecutive wins</p>
                </div>
              )}
            </div>
          </div>

          {/* Top 5 Standings */}
          <div className={`${theme.card} rounded-lg border ${theme.border} overflow-hidden`}>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className={`text-lg font-semibold ${theme.text}`}>Current Top 5 by Win %</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme.backgroundSecondary}>
                  <tr className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                    <th className="px-4 py-3 text-left">Rank</th>
                    <th className="px-4 py-3 text-left">Player</th>
                    <th className="px-4 py-3 text-center">Played</th>
                    <th className="px-4 py-3 text-center">W-L</th>
                    <th className="px-4 py-3 text-center">Win Rate</th>
                    <th className="px-4 py-3 text-center">Form</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {(stats.top5Standings || []).map(p => (
                    <tr key={p.id} className={theme.text}>
                      <td className="px-4 py-3"><RankBadge rank={p.rank} theme={theme} /></td>
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-center">{p.played}</td>
                      <td className="px-4 py-3 text-center">{formatRecord(p.wins, p.losses)}</td>
                      <td className="px-4 py-3 text-center"><span className="font-medium text-green-600 dark:text-green-400">{formatPercentage(p.winRate)}</span></td>
                      <td className="px-4 py-3 text-center"><FormDots form={p.form} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ── All Players ── */}
      {activeTab === 'players' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${theme.text}`}>All Participants ({allParticipants.length})</h3>
          </div>
          <div className={`${theme.card} rounded-lg border ${theme.border} overflow-hidden`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={theme.backgroundSecondary}>
                  <tr className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                    <SortableHeader label="Rank" sortKey="rank" sortConfig={sortConfig} onSort={handleSort} align="left" />
                    <SortableHeader label="Player" sortKey="name" sortConfig={sortConfig} onSort={handleSort} align="left" />
                    <SortableHeader label="Played" sortKey="played" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="W-L" sortKey="wins" sortConfig={sortConfig} onSort={handleSort} className="whitespace-nowrap" />
                    <SortableHeader label="Win %" sortKey="winRate" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Games" sortKey="gamesWon" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="PF-PA" sortKey="pointsFor" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Diff" sortKey="differential" sortConfig={sortConfig} onSort={handleSort} />
                    <SortableHeader label="Streak" sortKey="streak" sortConfig={sortConfig} onSort={handleSort} />
                    <th className="px-4 py-3 text-center">Form</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {allParticipants.map(p => (
                    <tr key={p.id} className={`${theme.text} hover:${theme.cardHover} transition-colors`}>
                      <td className="px-4 py-3"><RankBadge rank={p.rank} theme={theme} /></td>
                      <td className="px-4 py-3 font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-center">{p.played}</td>
                      <td className="px-4 py-3 text-center font-medium whitespace-nowrap">{formatRecord(p.wins, p.losses)}</td>
                      <td className="px-4 py-3 text-center"><span className="font-medium text-green-600 dark:text-green-400">{formatPercentage(p.winRate)}</span></td>
                      <td className="px-4 py-3 text-center text-sm">{p.gamesWon}-{p.gamesLost}</td>
                      <td className="px-4 py-3 text-center text-sm">{p.pointsFor}-{p.pointsAgainst}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`font-medium ${p.differential > 0 ? 'text-green-600 dark:text-green-400' : p.differential < 0 ? 'text-red-600 dark:text-red-400' : theme.textMuted}`}>
                          {p.differential > 0 ? '+' : ''}{p.differential}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                          p.currentStreak.type === 'win' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}>{p.currentStreak.type === 'win' ? 'W' : 'L'}{p.currentStreak.count}</span>
                      </td>
                      <td className="px-4 py-3 text-center"><FormDots form={p.form} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Shot Stats ── */}
      {activeTab === 'shots' && (
        <div className="space-y-6">
          {shotStats.length === 0 ? (
            <div className={`${theme.card} rounded-lg border ${theme.border} p-8`}>
              <p className={`text-center ${theme.textMuted}`}>
                No shot data yet. Shot stats are recorded when detailed scoring is used during matches.
              </p>
            </div>
          ) : (
            <>
              {/* Sort control */}
              <div className="flex items-center justify-between">
                <h3 className={`text-lg font-semibold ${theme.text}`}>Per-Player Shot Breakdown</h3>
                <select value={shotSort} onChange={e => setShotSort(e.target.value)}
                  className={`px-3 py-2 border ${theme.border} rounded-lg ${theme.card} ${theme.text} text-sm`}>
                  <option value="pointsWon">Sort by Points Won</option>
                  <option value="smash">Sort by Smash</option>
                  <option value="ace">Sort by Ace</option>
                  <option value="volley_winner">Sort by Volley Winner</option>
                  <option value="dink_winner">Sort by Dink Winner</option>
                  <option value="servingEfficiency">Sort by Serving Efficiency</option>
                  <option value="totalErrors">Sort by Total Errors</option>
                </select>
              </div>

              {/* Winning Shots Table */}
              <div className={`${theme.card} rounded-lg border ${theme.border} overflow-hidden`}>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <Target className="w-5 h-5 text-green-500" />
                  <h4 className={`font-semibold ${theme.text}`}>Winning Shots</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={theme.backgroundSecondary}>
                      <tr className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                        <th className="px-4 py-3 text-left sticky left-0 z-10" style={{ background: 'inherit' }}>Player</th>
                        <th className="px-4 py-3 text-center font-bold">Total</th>
                        {SHOT_COLS.map(c => (
                          <th key={c.key} className={`px-4 py-3 text-center ${c.color}`}>{c.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {shotStats.map((p, i) => (
                        <tr key={p.id} className={`${theme.text} hover:${theme.cardHover} transition-colors`}>
                          <td className="px-4 py-3 font-medium whitespace-nowrap sticky left-0 z-10" style={{ background: 'inherit' }}>
                            <div className="flex items-center gap-2">
                              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                i === 0 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : theme.backgroundSecondary
                              }`}>{i + 1}</span>
                              {p.name}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center font-bold text-green-600 dark:text-green-400">{p.pointsWon}</td>
                          {SHOT_COLS.map(c => (
                            <td key={c.key} className="px-4 py-3 text-center">
                              {p[c.key] > 0
                                ? <span className={`font-semibold ${c.color}`}>{p[c.key]}</span>
                                : <span className={theme.textMuted}>—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Errors & Serving Table */}
              <div className={`${theme.card} rounded-lg border ${theme.border} overflow-hidden`}>
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-orange-500" />
                  <h4 className={`font-semibold ${theme.text}`}>Errors &amp; Serving</h4>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className={theme.backgroundSecondary}>
                      <tr className={`text-xs font-medium ${theme.textMuted} uppercase`}>
                        <th className="px-4 py-3 text-left sticky left-0 z-10" style={{ background: 'inherit' }}>Player</th>
                        {ERROR_COLS.map(c => (
                          <th key={c.key} className={`px-4 py-3 text-center ${c.color}`}>{c.label}</th>
                        ))}
                        <th className="px-4 py-3 text-center text-blue-500">Srv Rly</th>
                        <th className="px-4 py-3 text-center text-blue-400">Srv Pts</th>
                        <th className="px-4 py-3 text-center text-indigo-500">Srv Eff%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {shotStats.map(p => {
                        const totalErrors = p.service_fault + p.forced_error + p.unforced_error + p.other_error;
                        return (
                          <tr key={p.id} className={`${theme.text} hover:${theme.cardHover} transition-colors`}>
                            <td className="px-4 py-3 font-medium whitespace-nowrap sticky left-0 z-10" style={{ background: 'inherit' }}>{p.name}</td>
                            {ERROR_COLS.map(c => (
                              <td key={c.key} className="px-4 py-3 text-center">
                                {p[c.key] > 0
                                  ? <span className={`font-semibold ${c.color}`}>{p[c.key]}</span>
                                  : <span className={theme.textMuted}>—</span>}
                              </td>
                            ))}
                            <td className="px-4 py-3 text-center">{p.ralliesServed}</td>
                            <td className="px-4 py-3 text-center">{p.pointsWhileServing}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-semibold ${
                                p.servingEfficiency >= 60 ? 'text-green-600 dark:text-green-400' :
                                p.servingEfficiency >= 40 ? 'text-yellow-600 dark:text-yellow-400' :
                                'text-red-600 dark:text-red-400'
                              }`}>{p.servingEfficiency}%</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Legend */}
              <div className={`p-4 ${theme.backgroundSecondary} rounded-lg text-sm ${theme.textMuted} space-y-1`}>
                <p><strong>Winning Shots:</strong> Smash · Ace · Lob · Drop Shot · Volley Winner · Dink Winner — credited to the player who hit the winning shot</p>
                <p><strong>Errors:</strong> Service Fault · Forced Error · Unforced Error · Other — charged to the player who made the error</p>
                <p><strong>Srv Rly</strong> = Rallies served &nbsp;|&nbsp; <strong>Srv Pts</strong> = Points won while serving &nbsp;|&nbsp; <strong>Srv Eff%</strong> = Serving efficiency (Srv Pts / Srv Rly)</p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TournamentPickleballStatsView;
